<template>
    <div class="ui-page">
        <ui-page-header title="Correspondências" subtitle="Correspondências enviadas aos credores, com o número de rastreio.">
            <v-btn v-if="$allowed('create')" id="btnImport" type="button" color="primary" outlined @click="openImport">
                <v-icon left small>mdi-file-upload-outline</v-icon>
                Importar planilha
            </v-btn>
            <v-btn v-if="$allowed('create')" id="btnNew" type="button" color="primary" depressed @click="openForm()">
                <v-icon left small>mdi-plus</v-icon>
                Nova correspondência
            </v-btn>
        </ui-page-header>

        <ui-table-card clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-text-field dense placeholder="Nome do credor" v-model="dataList.filter.name" prepend-inner-icon="mdi-magnify"
                    hide-details outlined clearable class="ui-toolbar__search" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-text-field dense placeholder="CPF/CNPJ" v-model="dataList.filter.registration" class="ui-toolbar__field"
                    hide-details outlined clearable @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-autocomplete dense :items="dataset.clients" placeholder="Todos os casos" class="ui-toolbar__field"
                    v-model="dataList.filter.client_id" item-value="id" item-text="name" :menu-props="{ offsetY: true }"
                    outlined hide-details clearable no-data-text="Nenhum caso encontrado" @change="applyFilters">
                </v-autocomplete>
                <v-text-field dense placeholder="Número de rastreio" v-model="dataList.filter.tracking_code" class="ui-toolbar__field"
                    hide-details outlined clearable @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
            </template>

            <v-data-table :headers="dataList.headers" :items="dataList.data" :loading="dataList.loading"
                :server-items-length="dataList.count" :options.sync="dataList.options"
                disable-sort hide-default-footer class="ui-clickable-rows"
                no-data-text="Nenhuma correspondência encontrada"
                loading-text="Carregando correspondências..." @click:row="openForm">

                <template v-slot:[`item.creditor_name`]="{ item }">
                    <div class="ui-cell-main">{{ item.creditor_name }}</div>
                    <div class="ui-cell-sub">{{ formatRegistration(item.creditor_registration) || '—' }}</div>
                </template>

                <template v-slot:[`item.client`]="{ item }">
                    {{ item.client ? item.client.name : '—' }}
                </template>

                <template v-slot:[`item.tracking_code`]="{ item }">
                    <span class="tracking-code">{{ item.tracking_code }}</span>
                    <v-btn icon x-small class="ml-1" title="Copiar número de rastreio" @click.stop="copyTracking(item.tracking_code)">
                        <v-icon x-small>mdi-content-copy</v-icon>
                    </v-btn>
                </template>

                <template v-slot:[`item.origin`]="{ item }">
                    <ui-badge :text="item.user_creditor_id ? 'QGC' : 'Manual'" :color="item.user_creditor_id ? 'primary' : 'grey'" />
                </template>

                <template v-slot:[`item.created_at`]="{ item }">
                    <div>{{ item.created_at | defaultDate("DD/MM/YYYY HH:mm") }}</div>
                    <div v-if="item.createdBy" class="ui-cell-sub">{{ item.createdBy.name }}</div>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        { text: 'Editar', icon: 'mdi-pencil-outline', handler: () => openForm(item) , disabled: !$allowed('edit') },
                        { text: 'Excluir', icon: 'mdi-delete-outline', danger: true, handler: () => remove(item) , disabled: !$allowed('delete') },
                    ]" />
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination :total="dataList.count" :page="dataList.options.page" :items-per-page="dataList.options.itemsPerPage"
                    :items-per-page-options="dataList.footerOptions.itemsPerPageOptions"
                    :disabled="dataList.loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
            </template>
        </ui-table-card>

        <!-- Cadastro / edicao -->
        <v-dialog v-model="form.dialog" max-width="640px" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <h3 class="ui-section-title">{{ form.id ? 'Editar correspondência' : 'Nova correspondência' }}</h3>
                </div>
                <div class="ui-card__body">
                    <v-form ref="form" @submit.prevent="save">
                        <v-row>
                            <v-col cols="12">
                                <v-autocomplete dense :items="dataset.clients" label="Caso"
                                    v-model="form.client_id" item-value="id" item-text="name"
                                    outlined hide-details="auto" :rules="[$rules.required]"
                                    no-data-text="Nenhum caso encontrado" @change="onFormClientChange">
                                </v-autocomplete>
                            </v-col>

                            <v-col cols="12">
                                <div class="text-body-2 mb-2">Credor</div>
                                <v-btn-toggle v-model="form.mode" mandatory dense color="primary">
                                    <v-btn small value="qgc">
                                        <v-icon left small>mdi-card-account-details-outline</v-icon> Buscar no QGC
                                    </v-btn>
                                    <v-btn small value="manual">
                                        <v-icon left small>mdi-pencil-outline</v-icon> Informar manualmente
                                    </v-btn>
                                </v-btn-toggle>
                            </v-col>

                            <!-- Credor do QGC do caso: preenche nome e CPF/CNPJ -->
                            <template v-if="form.mode === 'qgc'">
                                <v-col cols="12">
                                    <v-autocomplete dense outlined hide-details="auto"
                                        v-model="form.selectedCreditor"
                                        :items="creditorSearch.items"
                                        :loading="creditorSearch.loading"
                                        :search-input.sync="creditorSearch.term"
                                        :disabled="!form.client_id"
                                        :rules="[$rules.required]"
                                        item-value="user_creditor_id"
                                        :item-text="creditorLabel"
                                        return-object no-filter clearable
                                        label="Credor do QGC"
                                        :placeholder="form.client_id ? 'Digite o nome ou o CPF/CNPJ' : 'Selecione o caso primeiro'"
                                        :no-data-text="creditorSearch.term && creditorSearch.term.length >= 2 ? 'Nenhum credor encontrado no QGC deste caso' : 'Digite ao menos 2 caracteres'">
                                    </v-autocomplete>
                                </v-col>
                                <v-col v-if="form.selectedCreditor" cols="12">
                                    <div class="selected-creditor">
                                        <div class="font-weight-medium">{{ form.selectedCreditor.name }}</div>
                                        <div class="ui-cell-sub">
                                            {{ formatRegistration(form.selectedCreditor.registration) || 'Sem CPF/CNPJ no QGC' }}
                                            <span v-if="form.selectedCreditor.registration_fictitious"> (CPF/CNPJ fictício)</span>
                                        </div>
                                    </div>
                                </v-col>
                            </template>

                            <!-- Credor informado manualmente -->
                            <template v-else>
                                <v-col cols="12" sm="7">
                                    <v-text-field dense outlined hide-details="auto" label="Nome completo"
                                        v-model="form.creditor_name" :rules="[$rules.required]"></v-text-field>
                                </v-col>
                                <v-col cols="12" sm="5">
                                    <v-text-field dense outlined hide-details="auto" label="CPF/CNPJ"
                                        v-model="form.creditor_registration" maxlength="18"
                                        :rules="[$rules.required, registrationRule]"
                                        @blur="form.creditor_registration = formatRegistration(form.creditor_registration)">
                                    </v-text-field>
                                </v-col>
                            </template>

                            <v-col cols="12">
                                <v-text-field dense outlined hide-details="auto" label="Número de rastreio"
                                    v-model="form.tracking_code" :rules="[$rules.required]"
                                    hint="Ex.: AA123456789BR" persistent-hint
                                    @input="form.tracking_code = (form.tracking_code || '').toUpperCase()">
                                </v-text-field>
                            </v-col>
                        </v-row>
                    </v-form>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined @click="form.dialog = false" :disabled="form.saving">Cancelar</v-btn>
                    <v-btn v-if="$allowed('save')" color="primary" depressed @click="save" :loading="form.saving">Salvar</v-btn>
                </div>
            </v-card>
        </v-dialog>
        <!-- Importacao em lote: planilha com nome, CPF/CNPJ e codigo de rastreio -->
        <v-dialog v-model="importer.dialog" max-width="1000px" persistent scrollable>
            <v-card>
                <v-card-title>
                    Importar correspondências
                    <v-spacer />
                    <v-btn small text color="primary" @click="downloadTemplate">
                        <v-icon left small>mdi-file-download-outline</v-icon> Baixar modelo
                    </v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text style="max-height: 72vh;">
                    <v-alert dense text type="info" class="mt-4">
                        A planilha precisa das colunas <strong>Nome</strong>, <strong>CPF/CNPJ</strong> e
                        <strong>Código de rastreio</strong> (Excel ou CSV). Quando o CPF/CNPJ está no QGC do caso, a
                        correspondência é vinculada ao credor do QGC; se não estiver, usa o nome e o CPF/CNPJ da planilha.
                        Nada é gravado antes de você confirmar.
                    </v-alert>
                    <v-row>
                        <v-col cols="12" md="6">
                            <v-autocomplete dense :items="dataset.clients" label="Caso" v-model="importer.client_id"
                                item-value="id" item-text="name" outlined hide-details="auto"
                                no-data-text="Nenhum caso encontrado" @change="runPreview"></v-autocomplete>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-file-input dense outlined hide-details="auto" prepend-icon="" append-icon="mdi-paperclip"
                                accept=".xlsx,.xls,.csv" label="Planilha" v-model="importer.file"
                                @change="onImportFile"></v-file-input>
                        </v-col>
                    </v-row>

                    <v-alert v-if="importer.error" dense text type="error" class="mt-4 mb-0">{{ importer.error }}</v-alert>
                    <v-progress-linear v-if="importer.loading" indeterminate class="mt-4"></v-progress-linear>

                    <template v-if="importer.preview">
                        <div class="d-flex flex-wrap align-center mt-4" style="gap: 6px;">
                            <v-chip small label color="success" text-color="white">{{ importer.preview.summary.new }} a importar</v-chip>
                            <v-chip small label outlined color="primary">{{ importer.preview.summary.qgc }} vinculadas ao QGC</v-chip>
                            <v-chip small label outlined>{{ importer.preview.summary.manual }} fora do QGC</v-chip>
                            <v-chip v-if="importer.preview.summary.duplicate" small label color="grey" text-color="white">{{ importer.preview.summary.duplicate }} já cadastradas</v-chip>
                            <v-chip v-if="importer.preview.summary.duplicate_file" small label color="grey" text-color="white">{{ importer.preview.summary.duplicate_file }} repetidas na planilha</v-chip>
                            <v-chip v-if="importer.preview.summary.invalid" small label color="error" text-color="white">{{ importer.preview.summary.invalid }} com erro</v-chip>
                            <v-spacer />
                            <v-switch v-model="importer.onlyProblems" dense hide-details class="mt-0" label="Mostrar só as linhas que não serão importadas"></v-switch>
                        </div>
                        <v-data-table dense class="mt-3" :headers="importer.headers" :items="importPreviewRows"
                            :items-per-page="50" :footer-props="{ itemsPerPageOptions: [50, 100, -1], itemsPerPageText: 'Linhas por página:' }"
                            no-data-text="Nenhuma linha para mostrar">
                            <template v-slot:[`item.registration`]="{ item }">{{ formatRegistration(item.registration) || '—' }}</template>
                            <template v-slot:[`item.status`]="{ item }">
                                <v-chip x-small label :color="importStatusColor(item.status)" text-color="white">{{ importStatusLabel(item.status) }}</v-chip>
                                <div class="text-caption text--secondary">{{ item.message }}</div>
                            </template>
                        </v-data-table>
                    </template>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="importer.dialog = false" :disabled="importer.saving">Cancelar</v-btn>
                    <v-btn v-if="$allowed('create')" color="primary" depressed :loading="importer.saving"
                        :disabled="!importer.preview || !importer.preview.summary.new" @click="applyImport">
                        Importar {{ importer.preview ? importer.preview.summary.new : '' }} correspondência(s)
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import _ from "lodash";
import { downloadXlsx } from "@/common/util/export-xlsx.util";

const emptyForm = () => ({
    dialog: false,
    saving: false,
    id: null,
    client_id: null,
    mode: "qgc",
    selectedCreditor: null,
    creditor_name: "",
    creditor_registration: "",
    tracking_code: "",
});

export default {
    name: "CorrespondenceList",

    data: () => ({
        importer: {
            dialog: false,
            client_id: null,
            file: null,
            rows: [],
            preview: null,
            loading: false,
            saving: false,
            error: null,
            onlyProblems: false,
            headers: [
                { text: "Linha", value: "line", width: 70 },
                { text: "Credor", value: "name" },
                { text: "CPF/CNPJ", value: "registration" },
                { text: "Código de rastreio", value: "tracking_code" },
                { text: "Situação", value: "status" },
            ],
        },
        dataset: {
            clients: [],
        },

        form: emptyForm(),

        creditorSearch: {
            term: null,
            items: [],
            loading: false,
            timer: null,
        },

        dataList: {
            filter: {
                name: null,
                registration: null,
                client_id: null,
                tracking_code: null,
            },
            headers: [
                { text: "Credor", value: "creditor_name" },
                { text: "Caso", value: "client" },
                { text: "Número de rastreio", value: "tracking_code" },
                { text: "Origem", value: "origin" },
                { text: "Cadastrado em", value: "created_at" },
                { text: '', value: 'actions', sortable: false, align: 'end', width: 56 },
            ],
            data: [],
            loading: false,
            count: 0,
            options: { page: 1, itemsPerPage: 20 },
            loaded: { page: null, itemsPerPage: null },
            footerOptions: {
                itemsPerPageText: "Correspondências por página:",
                itemsPerPageOptions: [10, 20, 50, 100],
                pageText: "{0}-{1} de {2}",
            },
        },
    }),

    created() {
        this.loadClients();
    },

    computed: {
        hasFilters() {
            return Object.values(this.dataList.filter).some((value) => value);
        },
        importPreviewRows() {
            const rows = (this.importer.preview && this.importer.preview.rows) || [];
            return this.importer.onlyProblems ? rows.filter((row) => row.status !== "new") : rows;
        },
    },

    watch: {
        "dataList.options": {
            handler(value) {
                if (value.page === this.dataList.loaded.page && value.itemsPerPage === this.dataList.loaded.itemsPerPage) return;
                this.load();
            },
            deep: true,
        },

        // Busca no QGC com espera curta entre as teclas.
        "creditorSearch.term"(value) {
            if (this.form.selectedCreditor && value === this.creditorLabel(this.form.selectedCreditor)) return;
            clearTimeout(this.creditorSearch.timer);
            if (!value || value.length < 2 || !this.form.client_id) {
                this.creditorSearch.items = this.form.selectedCreditor ? [this.form.selectedCreditor] : [];
                return;
            }
            this.creditorSearch.timer = setTimeout(() => this.searchCreditors(value), 350);
        },
    },

    methods: {
        applyFilters() {
            this.load(true);
        },

        filterDebounced: _.debounce(function () {
            this.applyFilters();
        }, 400),

        // O watcher de options recarrega a lista.
        pageChanged(page) {
            this.dataList.options.page = page;
        },

        // A troca de tamanho e seguida de update:page(1), que recarrega.
        setPageSize(size) {
            this.dataList.options.itemsPerPage = size;
        },

        digits(value) {
            return String(value || "").replace(/\D/g, "");
        },

        formatRegistration(value) {
            const d = this.digits(value);
            if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
            if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
            return value || "";
        },

        registrationRule(value) {
            const length = this.digits(value).length;
            return length === 11 || length === 14 || "Informe um CPF (11 dígitos) ou CNPJ (14 dígitos)";
        },

        creditorLabel(item) {
            if (!item) return "";
            const doc = this.formatRegistration(item.registration);
            return doc ? `${item.name} — ${doc}` : item.name;
        },

        buildSearch() {
            return Object.fromEntries(
                Object.entries(this.dataList.filter).filter(([, v]) => v !== null && v !== undefined && v !== "")
            );
        },

        async load(resetPage = false) {
            if (resetPage) this.dataList.options.page = 1;
            this.dataList.loading = true;
            try {
                const result = await axios.get("/api/admin/correspondences", {
                    params: {
                        page: this.dataList.options.page,
                        limit: this.dataList.options.itemsPerPage,
                        search: this.buildSearch(),
                    },
                });
                const content = result.data.content;
                this.dataList.data = content.data || [];
                this.dataList.count = content.paging ? content.paging.total : 0;
                this.dataList.loaded = { page: this.dataList.options.page, itemsPerPage: this.dataList.options.itemsPerPage };
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            } finally {
                this.dataList.loading = false;
            }
        },

        async loadClients() {
            try {
                const result = await axios.get("/api/clients", { params: { start: 1, limit: 100000, search: {} } });
                const data = (result.data.content && result.data.content.data) || [];
                data.sort((a, b) => a.name.localeCompare(b.name));
                this.dataset.clients = data;
            } catch (error) {
                this.dataset.clients = [];
            }
        },

        clearFilters() {
            this.dataList.filter = { name: null, registration: null, client_id: null, tracking_code: null };
            this.applyFilters();
        },

        async searchCreditors(term) {
            this.creditorSearch.loading = true;
            try {
                const result = await axios.get("/api/admin/correspondences/creditors", {
                    params: { client_id: this.form.client_id, q: term },
                });
                this.creditorSearch.items = result.data.content || [];
            } catch (error) {
                this.creditorSearch.items = [];
            } finally {
                this.creditorSearch.loading = false;
            }
        },

        onFormClientChange() {
            // Credor do QGC pertence ao caso: trocar o caso limpa a selecao.
            this.form.selectedCreditor = null;
            this.creditorSearch.items = [];
            this.creditorSearch.term = null;
        },

        openForm(item = null) {
            this.form = emptyForm();
            this.creditorSearch.items = [];
            this.creditorSearch.term = null;
            if (item) {
                this.form.id = item.id;
                this.form.client_id = item.client_id;
                this.form.tracking_code = item.tracking_code;
                if (item.user_creditor_id) {
                    this.form.mode = "qgc";
                    this.form.selectedCreditor = {
                        user_creditor_id: item.user_creditor_id,
                        name: item.creditor_name,
                        registration: item.creditor_registration,
                    };
                    this.creditorSearch.items = [this.form.selectedCreditor];
                } else {
                    this.form.mode = "manual";
                    this.form.creditor_name = item.creditor_name;
                    this.form.creditor_registration = this.formatRegistration(item.creditor_registration);
                }
            }
            this.form.dialog = true;
            this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
        },

        async save() {
            if (this.$refs.form && !this.$refs.form.validate()) return;

            const payload = {
                client_id: this.form.client_id,
                tracking_code: this.form.tracking_code,
            };
            if (this.form.mode === "qgc") {
                payload.user_creditor_id = this.form.selectedCreditor ? this.form.selectedCreditor.user_creditor_id : null;
            } else {
                payload.creditor_name = this.form.creditor_name;
                payload.creditor_registration = this.digits(this.form.creditor_registration);
            }

            this.form.saving = true;
            try {
                if (this.form.id) {
                    await axios.patch(`/api/admin/correspondences/${this.form.id}`, payload);
                    this.$dialog.notify.success("Correspondência atualizada.", { position: "top-right", timeout: 3000 });
                } else {
                    await axios.post("/api/admin/correspondences", payload);
                    this.$dialog.notify.success("Correspondência cadastrada.", { position: "top-right", timeout: 3000 });
                }
                this.form.dialog = false;
                this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            } finally {
                this.form.saving = false;
            }
        },

        async remove(item) {
            const proceed = await this.$dialog.confirm({
                title: "Excluir correspondência",
                text: `Excluir a correspondência ${item.tracking_code} de ${item.creditor_name}?`,
                actions: { false: "Cancelar", true: "Excluir" },
            });
            if (!proceed) return;
            try {
                await axios.delete(`/api/admin/correspondences/${item.id}`);
                this.$dialog.notify.success("Correspondência excluída.", { position: "top-right", timeout: 3000 });
                // Se a pagina ficou vazia, volta uma.
                if (this.dataList.data.length === 1 && this.dataList.options.page > 1) this.dataList.options.page -= 1;
                else this.load();
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            }
        },

        // ---- Importacao em lote ----
        openImport() {
            this.importer = { ...this.importer, dialog: true, client_id: this.dataList.filter.client_id || null, file: null, rows: [], preview: null, error: null, onlyProblems: false };
        },

        normalizeHeader(value) {
            return String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]/g, "");
        },

        // Le a planilha no navegador e monta as linhas { line, name, registration, tracking_code }.
        async onImportFile(file) {
            this.importer.rows = [];
            this.importer.preview = null;
            this.importer.error = null;
            if (!file) return;
            try {
                const XLSX = await import("xlsx");
                const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                // Valor bruto: CPF/CNPJ gravado como numero viria em notacao cientifica no texto formatado.
                const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: "" })
                    .map((row) => row.map((cell) => (typeof cell === "number" && Number.isFinite(cell) ? cell.toFixed(0) : String(cell))));
                const headerIndex = matrix.findIndex((row) => row.some((cell) => String(cell).trim()));
                if (headerIndex < 0) throw new Error("A planilha está vazia.");
                const header = matrix[headerIndex].map((cell) => this.normalizeHeader(cell));
                const find = (names) => header.findIndex((cell) => names.includes(cell));
                const col = {
                    name: find(["nome", "credor", "nomedocredor", "nomecompleto", "razaosocial"]),
                    registration: find(["cpfcnpj", "cpf", "cnpj", "documento", "cpfoucnpj"]),
                    tracking: find(["codigoderastreio", "rastreio", "codigo", "numeroderastreio", "codigorastreio", "objeto", "tracking"]),
                };
                const missing = [];
                if (col.name < 0) missing.push("Nome");
                if (col.registration < 0) missing.push("CPF/CNPJ");
                if (col.tracking < 0) missing.push("Código de rastreio");
                if (missing.length) throw new Error(`Coluna(s) não encontrada(s): ${missing.join(", ")}. Use o modelo para conferir os nomes.`);

                this.importer.rows = matrix.slice(headerIndex + 1)
                    .map((row, index) => ({
                        line: headerIndex + index + 2,
                        name: String(row[col.name] || "").trim(),
                        registration: String(row[col.registration] || "").trim(),
                        tracking_code: String(row[col.tracking] || "").trim(),
                    }))
                    .filter((row) => row.name || row.registration || row.tracking_code);
                if (!this.importer.rows.length) throw new Error("A planilha não tem linhas preenchidas.");
                await this.runPreview();
            } catch (error) {
                this.importer.error = error.message || "Não foi possível ler a planilha.";
            }
        },

        async runPreview() {
            if (!this.importer.client_id || !this.importer.rows.length) return;
            this.importer.loading = true;
            this.importer.error = null;
            try {
                const result = await axios.post("/api/admin/correspondences/import/preview", {
                    client_id: this.importer.client_id,
                    rows: this.importer.rows,
                });
                this.importer.preview = result.data.content;
            } catch (error) {
                this.importer.preview = null;
                this.importer.error = this.$codes(error);
            } finally {
                this.importer.loading = false;
            }
        },

        async applyImport() {
            this.importer.saving = true;
            try {
                const result = await axios.post("/api/admin/correspondences/import/apply", {
                    client_id: this.importer.client_id,
                    rows: this.importer.rows,
                });
                const content = result.data.content || {};
                const left = (content.summary ? content.summary.total : 0) - (content.imported || 0);
                this.$dialog.notify.success(
                    `${content.imported || 0} correspondência(s) importada(s).${left ? ` ${left} linha(s) ficaram de fora.` : ""}`,
                    { position: "top-right", timeout: 5000 }
                );
                this.importer.dialog = false;
                this.dataList.filter.client_id = this.importer.client_id;
                this.load(true);
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            } finally {
                this.importer.saving = false;
            }
        },

        importStatusLabel(status) {
            return ({ new: "Importar", duplicate: "Já cadastrada", duplicate_file: "Repetida", invalid: "Com erro" })[status] || status;
        },

        importStatusColor(status) {
            return ({ new: "success", duplicate: "grey", duplicate_file: "grey", invalid: "error" })[status] || "grey";
        },

        async downloadTemplate() {
            await downloadXlsx([
                { "Nome": "Maria da Silva", "CPF/CNPJ": "123.456.789-09", "Código de rastreio": "AA123456789BR" },
                { "Nome": "Empresa Exemplo Ltda", "CPF/CNPJ": "11.222.333/0001-81", "Código de rastreio": "AB987654321BR" },
            ], "modelo-importacao-correspondencias", { sheetName: "Correspondências" });
        },

        async copyTracking(code) {
            try {
                await navigator.clipboard.writeText(code);
                this.$dialog.notify.info("Número de rastreio copiado.", { position: "top-right", timeout: 2000 });
            } catch (error) {
                // navegador sem permissao de area de transferencia: ignora
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.tracking-code {
    font-family: "Roboto Mono", Consolas, monospace;
    letter-spacing: 0.5px;
}

.selected-creditor {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 10px 12px;
    background: #fafafa;
}
</style>
